<template>
    <div class="ui-page">
        <ui-page-header title="Data room" subtitle="Documentos restritos aos habilitados que assinaram o termo de confidencialidade. Cada PDF sai com a marca d'água de quem baixou.">
            <v-btn v-if="processId && $allowed('competitive.dataroom')" color="primary" depressed @click="openEdit(null)"><v-icon left small>mdi-plus</v-icon>Adicionar documento</v-btn>
        </ui-page-header>

        <div class="ui-list-bar">
            <ui-pills v-model="view" :items="viewPills" />
        </div>

        <ui-table-card v-if="view === 'documents'">
            <template #toolbar>
                <competitive-process-picker v-model="processId" />
                <span class="ui-toolbar__spacer"></span>
                <span v-if="process" class="ui-small ui-muted">{{ process.nda_version ? `Termo de confidencialidade v${process.nda_version}` : "Termo de confidencialidade não publicado" }}</span>
            </template>
            <v-data-table :headers="docHeaders" :items="documents" :loading="loading" disable-sort disable-pagination hide-default-footer
                :no-data-text="processId ? 'Nenhum documento no data room' : 'Selecione um processo'" loading-text="Carregando...">
                <template v-slot:[`item.title`]="{ item }">
                    <div class="ui-cell-main">{{ item.title }}</div>
                    <div class="ui-cell-sub">{{ item.folder ? `${item.folder} · ` : "" }}{{ item.file_name }} · {{ formatBytes(item.file_size) }}</div>
                </template>
                <template v-slot:[`item.file_hash`]="{ item }"><span class="ui-mono ui-small" :title="item.file_hash">{{ item.file_hash ? `${item.file_hash.slice(0, 12)}…` : "—" }}</span></template>
                <template v-slot:[`item.access_count`]="{ item }">
                    <a v-if="item.access_count" href="#" @click.prevent="showAccess(item)">{{ item.access_count }} {{ item.access_count === 1 ? "acesso" : "acessos" }}</a>
                    <span v-else class="ui-muted">Nenhum acesso</span>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Abrir original', icon: 'mdi-open-in-new', handler: () => openOriginal(item) },
                        { text: 'Editar', icon: 'mdi-pencil-outline', handler: () => openEdit(item), disabled: !$allowed('competitive.dataroom') },
                        { text: 'Excluir', icon: 'mdi-delete-outline', handler: () => remove(item), danger: true, disabled: !$allowed('competitive.dataroom') },
                    ]" />
                </template>
            </v-data-table>
        </ui-table-card>

        <ui-table-card v-else clearable :can-clear="!!access.documentId" @clear="access.documentId = null">
            <template #toolbar>
                <competitive-process-picker v-model="processId" />
                <v-select v-model="access.documentId" :items="documents" item-value="id" item-text="title" placeholder="Todos os documentos" clearable dense outlined hide-details class="ui-toolbar__field" :menu-props="{ offsetY: true }"></v-select>
            </template>
            <v-data-table :headers="accessHeaders" :items="access.rows" :loading="access.loading" disable-sort disable-pagination hide-default-footer no-data-text="Nenhum acesso registrado" loading-text="Carregando...">
                <template v-slot:[`item.created_at`]="{ item }"><span class="ui-nowrap">{{ formatStamp(item.created_at, { seconds: true }) }}</span></template>
                <template v-slot:[`item.registration`]="{ item }">
                    <div class="ui-cell-main">{{ item.registration ? item.registration.name : "—" }}</div>
                    <div v-if="item.registration" class="ui-cell-sub">{{ item.registration.document_formatted }} · {{ item.registration.protocol }}</div>
                </template>
                <template v-slot:[`item.document`]="{ item }">{{ item.document ? item.document.title : "—" }}</template>
                <template v-slot:[`item.ip`]="{ item }">
                    <div class="ui-mono ui-small">{{ item.ip || "—" }}</div>
                    <div class="ui-cell-sub ui-mono" :title="item.file_hash">SHA-256 {{ item.file_hash ? `${item.file_hash.slice(0, 12)}…` : "—" }}</div>
                </template>
            </v-data-table>
            <template #footer>
                <div class="ui-pagination">
                    <span class="ui-small ui-muted">{{ access.total }} {{ access.total === 1 ? "acesso" : "acessos" }}</span>
                    <span class="ui-pagination__right">
                        <v-btn small outlined :disabled="access.page <= 1" @click="access.page -= 1">Anterior</v-btn>
                        <v-btn small outlined :disabled="access.page * 50 >= access.total" @click="access.page += 1">Próxima</v-btn>
                    </span>
                </div>
            </template>
        </ui-table-card>

        <v-dialog v-model="edit.open" max-width="560" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header"><h3 class="ui-section-title">{{ edit.id ? "Editar documento" : "Adicionar ao data room" }}</h3></div>
                <div class="ui-card__body">
                    <v-text-field v-model="edit.form.title" label="Título" outlined dense hide-details="auto" class="mb-4" maxlength="255"></v-text-field>
                    <v-combobox v-model="edit.form.folder" :items="folders" label="Pasta" outlined dense hide-details="auto" class="mb-4" placeholder="Ex.: Matrículas"></v-combobox>
                    <v-textarea v-model="edit.form.description" label="Descrição" outlined dense rows="2" auto-grow hide-details="auto" class="mb-4"></v-textarea>
                    <ui-file-drop v-if="!edit.id" :file="edit.file" accept="application/pdf" :max-size-mb="100" hint="PDF sem senha, de até 100 MB" :loading="edit.saving"
                        @select="edit.file = $event" @save="save" @error="(message) => notifyWarning(message)" />
                    <v-progress-linear v-if="edit.saving && edit.file" :value="edit.progress" height="4" rounded color="primary" class="mt-2"></v-progress-linear>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="edit.saving" @click="edit.open = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="edit.saving" :disabled="!String(edit.form.title || '').trim() || (!edit.id && !edit.file)" @click="save">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import CompetitiveProcessPicker from "@/components/competitive/CompetitiveProcessPicker.vue";
import processScope from "@/components/competitive/process-scope";
import { API, formatBytes, formatStamp, notifyError, notifySuccess, notifyWarning, openUrl, uploadForRecord } from "@/components/competitive/competitive.util";

export default {
    name: "CompetitiveDataroom",
    components: { CompetitiveProcessPicker },
    mixins: [processScope],
    data: () => ({
        view: "documents",
        process: null,
        documents: [],
        loading: false,
        edit: { open: false, id: null, file: null, saving: false, progress: 0, form: {} },
        access: { rows: [], total: 0, page: 1, documentId: null, loading: false },
        docHeaders: [
            { text: "Documento", value: "title" },
            { text: "SHA-256 do original", value: "file_hash", width: 170 },
            { text: "Acessos", value: "access_count", width: 130 },
            { text: "", value: "actions", align: "end", width: 48 },
        ],
        accessHeaders: [
            { text: "Quando", value: "created_at", width: 170 },
            { text: "Proponente", value: "registration" },
            { text: "Documento", value: "document" },
            { text: "IP e arquivo entregue", value: "ip", width: 200 },
        ],
    }),
    computed: {
        viewPills() {
            return [{ value: "documents", text: "Documentos", count: this.documents.length }, { value: "access", text: "Registro de acessos" }];
        },
        folders() {
            return [...new Set(this.documents.map((doc) => doc.folder).filter(Boolean))];
        },
    },
    watch: {
        view(value) {
            if (value === "access") this.loadAccess();
        },
        "access.documentId"() {
            this.access.page = 1;
            this.loadAccess();
        },
        "access.page"() {
            this.loadAccess();
        },
    },
    methods: {
        formatBytes,
        formatStamp,
        notifyWarning(message) {
            notifyWarning(this, message);
        },
        onProcess() {
            this.load();
            if (this.view === "access") this.loadAccess();
        },
        async load() {
            if (!this.processId) return;
            this.loading = true;
            try {
                const [process, documents] = await Promise.all([
                    axios.get(`${API}/processes/${this.processId}`),
                    axios.get(`${API}/processes/${this.processId}/dataroom`),
                ]);
                this.process = process.data.content;
                this.documents = documents.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },
        async loadAccess() {
            if (!this.processId) return;
            this.access.loading = true;
            try {
                const result = await axios.get(`${API}/processes/${this.processId}/dataroom-access`, { params: { document_id: this.access.documentId || undefined, page: this.access.page } });
                this.access.rows = result.data.content.data;
                this.access.total = result.data.content.total;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.access.loading = false;
            }
        },
        showAccess(item) {
            this.access.documentId = item.id;
            this.view = "access";
        },
        openEdit(item) {
            this.edit = {
                open: true,
                id: item ? item.id : null,
                file: null,
                saving: false,
                progress: 0,
                form: item ? { title: item.title, folder: item.folder || "", description: item.description || "" } : { title: "", folder: "", description: "" },
            };
        },
        async save() {
            this.edit.saving = true;
            try {
                if (this.edit.id) {
                    await axios.patch(`${API}/dataroom/${this.edit.id}`, this.edit.form);
                } else {
                    const file = await uploadForRecord(this.edit.file, (percent) => { this.edit.progress = percent; });
                    await axios.post(`${API}/processes/${this.processId}/dataroom`, { ...this.edit.form, ...file });
                }
                notifySuccess(this, "Documento salvo.");
                this.edit.open = false;
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.edit.saving = false;
            }
        },
        async openOriginal(item) {
            try {
                const result = await axios.get(`${API}/dataroom/${item.id}/file`);
                openUrl(result.data.content.url);
            } catch (error) {
                notifyError(this, error);
            }
        },
        async remove(item) {
            const confirmed = await this.$dialog.confirm({
                title: "Excluir do data room",
                text: `"${item.title}" deixa de aparecer para os habilitados. O registro de acessos continua. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Excluir", color: "error" } },
            });
            if (!confirmed) return;
            try {
                await axios.delete(`${API}/dataroom/${item.id}`);
                await this.load();
            } catch (error) {
                notifyError(this, error);
            }
        },
    },
};
</script>

<style lang="scss">
@import "../../components/competitive/competitive";
</style>
